/**
 * Pannable, zoomable image viewer for lightboxes
 */

import { useRef, useState, useCallback } from 'react';
import './ZoomableImage.css';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DOUBLE_CLICK_SCALE = 2.5;
const WHEEL_STEP = 0.25;

const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

/**
 * Image viewer supporting wheel zoom, pinch zoom, double-click/tap zoom, and drag panning
 * @param {Object} props - Component props
 * @param {string} props.src - Image URL
 * @param {string} props.alt - Alt text
 * @param {string} props.className - Additional class name
 * @returns {React.ReactElement} Zoomable image element
 */
export default function ZoomableImage({ src, alt, className = '' }) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const baseSizeRef = useRef(null);
  const dragRef = useRef(null);
  const pinchRef = useRef(null);

  const [scale, setScale] = useState(MIN_SCALE);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const clampOffset = useCallback((x, y, forScale) => {
    const container = containerRef.current;
    const base = baseSizeRef.current;
    if (!container || !base) return { x: 0, y: 0 };
    const containerRect = container.getBoundingClientRect();
    const overflowX = Math.max(0, (base.width * forScale - containerRect.width) / 2);
    const overflowY = Math.max(0, (base.height * forScale - containerRect.height) / 2);
    return {
      x: clamp(x, -overflowX, overflowX),
      y: clamp(y, -overflowY, overflowY)
    };
  }, []);

  const zoomTo = useCallback((newScale, cursor) => {
    const clampedScale = clamp(newScale, MIN_SCALE, MAX_SCALE);
    setScale(prevScale => {
      if (clampedScale === MIN_SCALE) {
        setOffset({ x: 0, y: 0 });
        return clampedScale;
      }
      const ratio = clampedScale / prevScale;
      setOffset(prevOffset => {
        const rawX = cursor.x * (1 - ratio) + prevOffset.x * ratio;
        const rawY = cursor.y * (1 - ratio) + prevOffset.y * ratio;
        return clampOffset(rawX, rawY, clampedScale);
      });
      return clampedScale;
    });
  }, [clampOffset]);

  const handleImageLoad = useCallback(() => {
    if (imgRef.current) {
      const rect = imgRef.current.getBoundingClientRect();
      baseSizeRef.current = { width: rect.width, height: rect.height };
    }
    setScale(MIN_SCALE);
    setOffset({ x: 0, y: 0 });
  }, []);

  const getCursorFromEvent = useCallback((clientX, clientY) => {
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: clientX - rect.left - rect.width / 2,
      y: clientY - rect.top - rect.height / 2
    };
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const cursor = getCursorFromEvent(e.clientX, e.clientY);
    const delta = e.deltaY < 0 ? WHEEL_STEP : -WHEEL_STEP;
    zoomTo(scale + delta, cursor);
  }, [scale, zoomTo, getCursorFromEvent]);

  const handleDoubleClick = useCallback((e) => {
    const cursor = getCursorFromEvent(e.clientX, e.clientY);
    zoomTo(scale > MIN_SCALE ? MIN_SCALE : DOUBLE_CLICK_SCALE, cursor);
  }, [scale, zoomTo, getCursorFromEvent]);

  const handleMouseDown = useCallback((e) => {
    if (scale <= MIN_SCALE) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, offsetStart: offset };
    setIsDragging(true);
  }, [scale, offset]);

  const handleMouseMove = useCallback((e) => {
    if (!dragRef.current) return;
    const { startX, startY, offsetStart } = dragRef.current;
    const next = clampOffset(offsetStart.x + (e.clientX - startX), offsetStart.y + (e.clientY - startY), scale);
    setOffset(next);
  }, [scale, clampOffset]);

  const endDrag = useCallback(() => {
    dragRef.current = null;
    setIsDragging(false);
  }, []);

  const touchDistance = (touches) => {
    const [a, b] = touches;
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  };

  const touchMidpoint = (touches) => {
    const [a, b] = touches;
    return { clientX: (a.clientX + b.clientX) / 2, clientY: (a.clientY + b.clientY) / 2 };
  };

  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      pinchRef.current = {
        startDistance: touchDistance(e.touches),
        startScale: scale
      };
      dragRef.current = null;
    } else if (e.touches.length === 1 && scale > MIN_SCALE) {
      const touch = e.touches[0];
      dragRef.current = { startX: touch.clientX, startY: touch.clientY, offsetStart: offset };
    }
  }, [scale, offset]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      e.preventDefault();
      const { startDistance, startScale } = pinchRef.current;
      const newScale = clamp(startScale * (touchDistance(e.touches) / startDistance), MIN_SCALE, MAX_SCALE);
      const mid = touchMidpoint(e.touches);
      const cursor = getCursorFromEvent(mid.clientX, mid.clientY);
      zoomTo(newScale, cursor);
    } else if (e.touches.length === 1 && dragRef.current) {
      const touch = e.touches[0];
      const { startX, startY, offsetStart } = dragRef.current;
      const next = clampOffset(offsetStart.x + (touch.clientX - startX), offsetStart.y + (touch.clientY - startY), scale);
      setOffset(next);
    }
  }, [scale, clampOffset, zoomTo, getCursorFromEvent]);

  const handleTouchEnd = useCallback((e) => {
    if (e.touches.length < 2) pinchRef.current = null;
    if (e.touches.length === 0) dragRef.current = null;
  }, []);

  const handleZoomInBtn = () => zoomTo(scale + WHEEL_STEP * 2, { x: 0, y: 0 });
  const handleZoomOutBtn = () => zoomTo(scale - WHEEL_STEP * 2, { x: 0, y: 0 });
  const handleResetBtn = () => zoomTo(MIN_SCALE, { x: 0, y: 0 });

  const canPan = scale > MIN_SCALE;

  return (
    <div
      ref={containerRef}
      className={`zoomable-image ${className}`}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        onLoad={handleImageLoad}
        onDoubleClick={handleDoubleClick}
        draggable={false}
        className={`zoomable-image__img${canPan ? ' zoomable-image__img--pannable' : ''}${isDragging ? ' zoomable-image__img--dragging' : ''}`}
        style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }}
      />
      <div className="zoomable-image__controls">
        <button type="button" onClick={handleZoomOutBtn} disabled={scale <= MIN_SCALE} aria-label="Zoom out">
          &minus;
        </button>
        <span className="zoomable-image__zoom-level">{Math.round(scale * 100)}%</span>
        <button type="button" onClick={handleZoomInBtn} disabled={scale >= MAX_SCALE} aria-label="Zoom in">
          +
        </button>
        {scale > MIN_SCALE && (
          <button type="button" onClick={handleResetBtn} className="zoomable-image__reset" aria-label="Reset zoom">
            Reset
          </button>
        )}
      </div>
    </div>
  );
}
